import { db } from '../firebase';
import { 
  collection, 
  addDoc, 
  query, 
  where, 
  getDocs, 
  updateDoc,
  doc,
  orderBy,
  onSnapshot,
  serverTimestamp
} from 'firebase/firestore';

class NotificationService {
  // Send a notification to a specific user
  static async sendNotification(userId, notification) {
    try {
      console.log(`Sending notification to user ${userId}:`, notification);
      
      if (!userId) {
        console.error("No userId provided for notification");
        return false;
      }
      
      const notificationData = {
        userId,
        message: notification.message,
        type: notification.type || 'info',
        read: false,
        createdAt: serverTimestamp(),
        link: notification.link || null,
      };
      
      const docRef = await addDoc(collection(db, 'notifications'), notificationData);
      console.log("Notification added with ID:", docRef.id);
      return true;
    } catch (error) {
      console.error('Error sending notification:', error);
      return false;
    }
  }

  // Send a notification to all users (admin only)
  static async broadcastNotification(notification) {
    try {
      console.log("Broadcasting notification to all users:", notification);
      
      const notificationData = {
        broadcast: true,
        message: notification.message,
        type: notification.type || 'info',
        read: false,
        createdAt: serverTimestamp(),
        link: notification.link || null,
      };
      
      const docRef = await addDoc(collection(db, 'notifications'), notificationData);
      console.log("Broadcast notification added with ID:", docRef.id);
      return true;
    } catch (error) {
      console.error('Error broadcasting notification:', error);
      return false;
    }
  }

  // Get notifications for a specific user
  static getUserNotifications(userId, callback) {
    if (!userId) {
      console.error("No userId provided for getting notifications");
      return null;
    }
    
    console.log("Setting up notification listeners for user:", userId);
    
    try {
      // Query for user-specific notifications
      const userQuery = query(
        collection(db, 'notifications'),
        where('userId', '==', userId),
        orderBy('createdAt', 'desc')
      );
      
      // Query for broadcast notifications
      const broadcastQuery = query(
        collection(db, 'notifications'),
        where('broadcast', '==', true),
        orderBy('createdAt', 'desc')
      );
      
      // Listen for user-specific notifications
      const userUnsubscribe = onSnapshot(userQuery, (snapshot) => {
        console.log(`Got ${snapshot.docs.length} user notifications`);
        
        const userNotifications = snapshot.docs.map(doc => ({
          id: doc.id,
          ...doc.data(),
          timestamp: doc.data().createdAt ? doc.data().createdAt.toDate() : new Date()
        }));
        
        // Listen for broadcast notifications
        onSnapshot(broadcastQuery, (broadcastSnapshot) => {
          console.log(`Got ${broadcastSnapshot.docs.length} broadcast notifications`);
          
          const broadcastNotifications = broadcastSnapshot.docs.map(doc => ({
            id: doc.id,
            ...doc.data(),
            timestamp: doc.data().createdAt ? doc.data().createdAt.toDate() : new Date()
          }));
          
          // Combine and sort all notifications
          const allNotifications = [...userNotifications, ...broadcastNotifications]
            .sort((a, b) => b.timestamp - a.timestamp);
          
          console.log("Combined notifications:", allNotifications.length);
          callback(allNotifications);
        });
      });
      
      // Return unsubscribe function
      return () => {
        console.log("Unsubscribing from notification listeners");
        userUnsubscribe();
      };
    } catch (error) {
      console.error("Error setting up notification listeners:", error);
      return null;
    }
  }

  // Mark notification as read
  static async markAsRead(notificationId) {
    try {
      console.log("Marking notification as read:", notificationId);
      
      if (!notificationId) {
        console.error("No notificationId provided");
        return false;
      }
      
      const notificationRef = doc(db, 'notifications', notificationId);
      await updateDoc(notificationRef, {
        read: true
      });
      
      console.log("Notification marked as read");
      return true;
    } catch (error) {
      console.error('Error marking notification as read:', error);
      return false;
    }
  }

  // Mark all notifications as read for a user
  static async markAllAsRead(userId) {
    try {
      console.log("Marking all notifications as read for user:", userId);
      
      if (!userId) {
        console.error("No userId provided");
        return false;
      }
      
      // Get all unread notifications for the user
      const userQuery = query(
        collection(db, 'notifications'),
        where('userId', '==', userId),
        where('read', '==', false)
      );
      
      const broadcastQuery = query(
        collection(db, 'notifications'),
        where('broadcast', '==', true),
        where('read', '==', false)
      );
      
      const userSnapshot = await getDocs(userQuery);
      const broadcastSnapshot = await getDocs(broadcastQuery);
      
      console.log(`Found ${userSnapshot.docs.length} unread user notifications and ${broadcastSnapshot.docs.length} unread broadcast notifications`);
      
      // Update all notifications
      const updatePromises = [];
      
      userSnapshot.forEach(document => {
        updatePromises.push(updateDoc(doc(db, 'notifications', document.id), { read: true }));
      });
      
      broadcastSnapshot.forEach(document => {
        updatePromises.push(updateDoc(doc(db, 'notifications', document.id), { read: true }));
      });
      
      await Promise.all(updatePromises);
      console.log("All notifications marked as read");
      return true;
    } catch (error) {
      console.error('Error marking all notifications as read:', error);
      return false;
    }
  }
}

export default NotificationService; 